// /home/heliowallet/frontend/components/wallet/AccountCard.tsx
'use client';

import { useState } from 'react';
import { useAccount, useChainId, useDisconnect } from 'wagmi';
import Identicon from '@/components/Identicon';
import QRCode from 'react-qr-code';
import {
  ClipboardDocumentIcon,
  GlobeAltIcon,
  KeyIcon,
  QrCodeIcon,
  ArrowPathIcon,
  CogIcon,
  SignalIcon,
  ChevronDownIcon,
  BoltIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { clearStoredWallet } from '@/connectors/customWalletConnector';

interface AccountCardProps {
  chainInfo: any;
  selectedRpc: string | null;
  rpcHealth?: any;
  onOpenChainSelector: () => void;
  onOpenRpcDialog: () => void;
  onTestBestRpc: () => void;
  isTestingRpc: boolean;
}

export default function AccountCard({
  chainInfo,
  selectedRpc,
  rpcHealth,
  onOpenChainSelector,
  onOpenRpcDialog,
  onTestBestRpc,
  isTestingRpc
}: AccountCardProps) {
  const { address, connector } = useAccount();
  const chainId = useChainId();
  const { disconnect } = useDisconnect();
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const isImportedWallet = connector?.id === 'customWallet';

  const copyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      setCopied(true);
      toast.success('Address copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLogout = () => {
    if (isImportedWallet) {
      // Clear stored wallet data
      clearStoredWallet();
      toast.success('Imported wallet disconnected');
    }
    // Disconnect from wagmi
    disconnect();
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const getRpcHealthIcon = () => {
    if (!rpcHealth) return null;
    
    if (rpcHealth.score >= 80) {
      return <SignalIcon className="h-4 w-4 text-green-500" title="Excellent connection" />;
    } else if (rpcHealth.score >= 50) {
      return <SignalIcon className="h-4 w-4 text-yellow-500" title="Good connection" />;
    } else {
      return <SignalIcon className="h-4 w-4 text-red-500" title="Poor connection" />;
    }
  };

  const getExplorerUrl = () => {
    if (chainInfo?.explorers?.[0]) {
      return `${chainInfo.explorers[0].url}/address/${address}`;
    }
    return `https://blockscan.com/address/${address}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden h-full flex flex-col">
      {/* Header with Account Info */}
      <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Identicon address={address || ''} size={48} />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Account Address
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <p className="font-mono text-xs sm:text-sm text-gray-700 dark:text-gray-300 break-all">
                  {address}
                </p>
                {isImportedWallet && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded text-xs whitespace-nowrap">
                    <KeyIcon className="h-3 w-3" />
                    <span className="hidden sm:inline">Imported</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1 sm:gap-2 mt-4 flex-wrap">
          <button
            onClick={() => window.open(getExplorerUrl(), '_blank')}
            className="px-2 sm:px-3 py-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-sm shadow-sm"
          >
            <GlobeAltIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Explorer</span>
          </button>
          <button
            onClick={() => setShowQR(!showQR)}
            className="px-2 sm:px-3 py-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-sm shadow-sm"
          >
            <QrCodeIcon className="h-4 w-4" />
            <span className="hidden sm:inline">QR code</span>
          </button>
          <button
            onClick={copyAddress}
            className="px-2 sm:px-3 py-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-sm shadow-sm"
          >
            {copied ? (
              <>
                <CheckIcon className="h-4 w-4 text-green-500" />
                <span className="hidden sm:inline">Copied</span>
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Copy</span>
              </>
            )}
          </button>
          {isImportedWallet && (
            <button
              onClick={handleLogout}
              className="px-2 sm:px-3 py-2 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-sm shadow-sm"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          )}
        </div>
      </div>

      {/* QR Code */}
      {showQR && (
        <div className="p-6 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
          <div className="flex flex-col items-center">
            <div className="p-4 bg-white rounded-lg mb-3">
              {address && (
                <QRCode
                  value={address}
                  size={160}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  viewBox={`0 0 160 160`}
                />
              )}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 text-center font-mono break-all">
              {address}
            </p>
          </div>
        </div>
      )}

      {/* Network & RPC */}
      <div className="p-6 space-y-4 flex-1">
        {/* Network */}
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Connected Network</p>
          <button
            onClick={onOpenChainSelector}
            className="w-full p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            <div className="flex items-center gap-3">
              {chainInfo?.icon ? (
                <img src={chainInfo.icon} alt="" className="h-6 w-6 rounded-full" />
              ) : (
                <div className="h-6 w-6 bg-gray-300 dark:bg-gray-600 rounded-full" />
              )}
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-white">
                  {chainInfo?.name || `Chain ${chainId}`}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Chain ID: {chainId}
                </p>
              </div>
            </div>
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* RPC Endpoint */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">RPC Endpoint</p>
            <div className="flex items-center gap-2">
              {getRpcHealthIcon()}
              {rpcHealth && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {rpcHealth.latency === Infinity ? 'Failed' : `${rpcHealth.latency}ms`}
                </span>
              )}
            </div>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs sm:text-sm font-mono text-gray-700 dark:text-gray-300 truncate flex-1">
                {selectedRpc ? new URL(selectedRpc).hostname : 'Default RPC'}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={onTestBestRpc}
                  disabled={isTestingRpc}
                  className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Test best RPC"
                >
                  <BoltIcon className={`h-4 w-4 ${isTestingRpc ? 'animate-pulse' : ''}`} />
                </button>
                <button
                  onClick={onOpenRpcDialog}
                  className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Configure RPC"
                >
                  <CogIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}